import { Responsive, useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { useMessagesRead, useMessagesScroll } from './hooks';
import { ChatScrollLayout } from '../../components/ChatScrollLayout';
import { ChatUnreadMessagesButton } from '../../components/ChatUnreadMessagesButton';
import { ScrollButton } from '../../components/ScrollButton';
import { currentOpenChatSelector } from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { EDeviceType } from '../../types/deviceType';
import { ChatArchiveInfoContainer } from '../ChatArchiveInfoContainer';
import { ChatPageContainer } from '../ChatPageContainer';
import { ChatRemovalDateContainer } from '../ChatRemovalDateContainer';
import { ScrollContainer } from '../ScrollContainer';

type TProps = {
  scrollRef: React.RefObject<ScrollContainer>;
  scrollToBottom: (isButtonClickScroll?: boolean) => void;
};

export const ChatScrollContainer: React.FC<TProps> = ({ scrollRef, scrollToBottom }) => {
  const [isScrollToBottomButtonVisible, setIsScrollToBottomButtonVisible] = React.useState<boolean>(false);

  const currentChat = useSelector(currentOpenChatSelector);

  const device = useDeviceType();
  const isMobile = device !== EDeviceType.Desktop;
  const isLoading = currentChat?.status === ERequestApiStatus.Pending;

  const { unreadMessagesCount, setRefInMessagesMap, markMessagesAsRead } = useMessagesRead();
  const { getNextMessagesPage } = useMessagesScroll(scrollToBottom);

  const lastPageMessagesLength = React.useMemo(() => {
    if (currentChat?.pages && currentChat.pages.length) {
      const lastPageMessages = currentChat.pages[currentChat.pages.length - 1]?.messages;

      return lastPageMessages?.length;
    }

    return 0;
  }, [currentChat?.pages]);

  React.useEffect(() => {
    markMessagesAsRead();
  }, [markMessagesAsRead, lastPageMessagesLength]);

  const handlePageScroll = React.useCallback(
    (scrollTop: number) => {
      getNextMessagesPage(scrollTop);
    },
    [getNextMessagesPage],
  );

  const pagesNode = React.useMemo(() => {
    return currentChat?.pages.map(page => (
      <ChatPageContainer key={page.date} page={page} setRefInMessagesMap={setRefInMessagesMap} />
    ));
  }, [currentChat?.pages, setRefInMessagesMap]);

  return (
    <>
      <ScrollContainer
        ref={scrollRef}
        showScrollBottomButton={isMobile}
        onButtonVisible={setIsScrollToBottomButtonVisible}
        onScroll={handlePageScroll}
      >
        <ChatScrollLayout
          isLoading={isLoading}
          pagesNode={pagesNode}
          removalDateNode={<ChatRemovalDateContainer />}
          archiveInfoNode={<ChatArchiveInfoContainer />}
        />
      </ScrollContainer>
      {unreadMessagesCount > 0 && (
        <ChatUnreadMessagesButton counter={unreadMessagesCount} onClick={() => scrollToBottom(true)} />
      )}
      {isScrollToBottomButtonVisible && (
        <Responsive phone tablet>
          <ScrollButton onClick={() => scrollToBottom(true)} />
        </Responsive>
      )}
    </>
  );
};
